import React from "react";
import {IPagedList, SortDirection} from "@renta-apps/athenaeum-toolkit";
import {BaseComponent, Justify, PageRoute, PageRouteProvider, TextAlign} from "@renta-apps/athenaeum-react-common";
import {CellAction, CellModel, ColumnDefinition, ColumnType, Grid, GridHoveringType, GridModel, GridOddType} from "@renta-apps/athenaeum-react-components";
import WorkOrderCreationRequestModel from "../../../models/server/WorkOrderCreationRequestModel";
import WorkOrderCreationRequestsToolbarModel from "@/pages/WorkOrderCreationRequests/WorkOrderCreationRequestsToolbar/WorkOrderCreationRequestsToolbarModel";
import GetWorkOrderCreationRequestsRequest from "@/models/server/requests/GetWorkOrderCreationRequestsRequest";
import RentaTaskConstants from "@/helpers/RentaTaskConstants";
import UserInteractionDataStorage from "@/providers/UserInteractionDataStorage";
import Localizer from "../../../localization/Localizer";
import WorkOrderCreationRequestProvider from "@/providers/WorkOrderCreationRequestProvider";
import {ActionType, WorkOrderContractType} from "@/models/Enums";
import PageDefinitions from "@/providers/PageDefinitions";
import EnumProvider from "@/providers/EnumProvider";

import styles from "./WorkOrderCreationRequestsPanel.module.scss";

export interface IWorkOrderCreationRequestsPanelProps {
    filters: WorkOrderCreationRequestsToolbarModel;
}

interface IWorkOrderCreationRequestsPanelState {
}

export default class WorkOrderCreationRequestsPanel extends BaseComponent<IWorkOrderCreationRequestsPanelProps, IWorkOrderCreationRequestsPanelState> {

    state: IWorkOrderCreationRequestsPanelState = {
    };

    private readonly _workOrderCreationRequestsGridRef: React.RefObject<Grid<WorkOrderCreationRequestModel>> = React.createRef();

    private readonly _workOrderCreationRequestsColumns: ColumnDefinition[] = [
        {
            header: Localizer.tasksNameLanguageItemName,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.name),
            type: ColumnType.Text,
            sorting: true,
            editable: false,
            minWidth: 200,
            init: (cell) => this.initNameColumn(cell),
            settings: {
                descriptionAccessor: nameof<WorkOrderCreationRequestModel>(wo => wo.description),
                descriptionTitle: Localizer.invoicesInvoiceCommentLanguageItemName,
            },
        },
        {
            header: Localizer.genericExternalId,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.externalId),
            maxWidth: 150,
            sorting: true,
            editable: false,
        },
        {
            header: Localizer.constructionSitePanelERPConstructionSiteId,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.erpConstructionSiteId),
            maxWidth: 150,
            sorting: true,
            editable: false,
        },
        {
            header: Localizer.genericWorkOrderTypeLabel,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.workOrderType),
            maxWidth: 150,
            sorting: true,
            editable: false,
        },
        {
            header: Localizer.workOrderContractTypeDropdownLabel,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.contractType),
            maxWidth: 150,
            sorting: true,
            editable: false,
            transform(cell: CellModel<any>): string {
                const isValidType: boolean = Object.values(WorkOrderContractType).includes(cell.model.contractType ?? -1); 
                return isValidType ? EnumProvider.getWorkOrderContractTypeText(cell.model.contractType): "";
            },
        },
        {
            header: Localizer.enumFormHeaderTypeWorkOrderManagerEmail,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.managerEmail),
            maxWidth: 150,
            sorting: true,
            editable: false,
        },
        {
            header: Localizer.genericApprover,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.approverLastname),
            transform: (cell: CellModel<WorkOrderCreationRequestModel>) => {
                cell.readonly = true;
                return `${cell.model.approverFirstname} ${cell.model.approverLastname}`.trim()
            },
            minWidth: 150,
            settings: {
                descriptionAccessor: nameof<WorkOrderCreationRequestModel>(wo => wo.approverPhone),
                infoTransform(cell: CellModel<any>): string {
                    return `${cell.model.approverEmail} ${cell.model.approverPhone}`.trim()
                },
                descriptionJustify: Justify.Right,
                descriptionIcon: "fas info",
            },
        },
        {
            header: Localizer.genericOrderer,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.ordererLastname),
            transform: (cell: CellModel<WorkOrderCreationRequestModel>) => {
                cell.readonly = true;
                return `${cell.model.ordererFirstname} ${cell.model.ordererLastname}`.trim()
            },
            minWidth: 150,
            settings: {
                descriptionAccessor: nameof<WorkOrderCreationRequestModel>(wo => wo.approverPhone),
                infoTransform(cell: CellModel<any>): string {
                    return `${cell.model.ordererEmail} ${cell.model.ordererPhone}`.trim()
                },
                descriptionJustify: Justify.Right,
                descriptionIcon: "fas info",
            },
        },
        {
            header: Localizer.workOrderFixedCostLabel,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.fixedCost),
            type: ColumnType.Number,
            textAlign: TextAlign.Left,
            minWidth: 110,
            init: (cell) => {
                cell.readonly = true;
            },
        },
        {
            header: Localizer.genericCreatedAt,
            accessor: nameof<WorkOrderCreationRequestModel>(wo => wo.createdAt),
            type: ColumnType.Date,
            format: "D",
            textAlign: TextAlign.Left,
            minWidth: 110,
            isDefaultSorting: true,
            sorting: true,
            init: (cell) => {
                cell.readonly = true;
            },
        },
        {
            header: Localizer.genericActions,
            textAlign: TextAlign.Left,
            maxWidth: 250,
            init: (cell) => this.initWorkOrderOperations(cell),
            route: WorkOrderCreationRequestsPanel.getWorkOrderFromRequestRoute,
            actions: [
                {
                    name: nameof<WorkOrderCreationRequestModel>(wo => wo.name),
                    title: Localizer.addTaskModalCreateTask,
                    icon: "fal external-link-square",
                    type: ActionType.Create,
                    callback: async (cell) => await this.triggerRouteAsync(cell.route)
                },
            ]
        },
    ];

    private initWorkOrderOperations(cell: CellModel<WorkOrderCreationRequestModel>): void {
        const operations: Record<string, boolean> = this.getActions();
        const createAction: CellAction<WorkOrderCreationRequestModel> = cell.actions[0];
        createAction.visible = operations.create;
    }

    private getActions(
    ): Record<string, boolean> {
        return {
            create: true
        };
    }

    private static getWorkOrderFromRequestRoute(cell: CellModel<WorkOrderCreationRequestModel>): PageRoute {
        return (cell.model.id)
            ? PageDefinitions.workOrderRoute(null, null, cell.model.id)
            // Grid types do not allow the returned value to be null, but it has been like this forever.
            : null as any;
    }

    private async triggerRouteAsync(route: PageRoute | null): Promise<void> {
        if (route) {
            await PageRouteProvider.redirectAsync(route);
        }
    }

    private async getWorkOrderCreationRequestsAsync(
        pageNumber: number,
        pageSize: number,
        sortColumnName: string | null,
        sortDirection: SortDirection | null
    ): Promise<IPagedList<WorkOrderCreationRequestModel>> {

        UserInteractionDataStorage.setFilters(sortColumnName, "WorkOrderCreationRequests.SortColumn");
        UserInteractionDataStorage.setFilters(sortDirection, "WorkOrderCreationRequests.SortDirection");

        const request: GetWorkOrderCreationRequestsRequest = {
            pageNumber: pageNumber,
            pageSize: pageSize,
            sortColumnName: sortColumnName,
            sortDirection: sortDirection,
            from: this.props.filters.from,
            to: this.props.filters.to,
            search: this.props.filters.search,
        }

        return await WorkOrderCreationRequestProvider.getWorkOrderCreationRequestsPagedListAsync(this.WorkOrderCreationRequestsGrid, request);
    }

    private initNameColumn(cell: CellModel<WorkOrderCreationRequestModel>): void {
        cell.readonly = true;
        cell.descriptionReadonly = (cell.isReadonly);
    }

    public get WorkOrderCreationRequestsGrid(): GridModel<WorkOrderCreationRequestModel> {
        return this._workOrderCreationRequestsGridRef.current!.model;
    }

    private get sortColumn(): string {
        return UserInteractionDataStorage.getFilters("activationDate", "WorkOrderCreationRequests.SortColumn");
    }

    private get sortDirection(): SortDirection {
        return UserInteractionDataStorage.getFilters(SortDirection.Asc, "WorkOrderCreationRequests.SortDirection");
    }

    public render(): React.ReactNode {
        return (
            <>
                <Grid id="workOrderCreationRequestsPanelGrid"
                      autoToggle
                      ref={this._workOrderCreationRequestsGridRef}
                      className={this.css(styles.requestsTable)}
                      hovering={GridHoveringType.Row}
                      odd={GridOddType.None}
                      minWidth="auto"
                      noDataText={Localizer.genericNoData}
                      columns={this._workOrderCreationRequestsColumns}
                      fetchData={async (_, pageNumber, pageSize, sortColumnName, sortDirection) => await this.getWorkOrderCreationRequestsAsync(pageNumber, pageSize, sortColumnName, sortDirection)}
                      defaultSortColumn={this.sortColumn}
                      defaultSortDirection={this.sortDirection}
                      pagination={RentaTaskConstants.paginationNumber}
                />
            </>
        );
    }
}